import React, { useEffect, useState, useRef } from "react";
import LcFetch from "lib/shared/fetch";
import helpers from "helpers/base";
import FullPaymentForm from "./FullPaymentForm";
import { genFormKey, setNonWalletErrors } from "./util";

export function SignUpPaymentForm(props) {
  const [paypalActions, setPaypalActions] = useState({});
  const [paypalErrorMessage, setPaypalErrorMessage] = useState();
  const [stripeErrorMessage, setStripeErrorMessage] = useState();
  const [disabled, setDisabled] = useState(false);
  const [processingStripe, setProcessingStripe] = useState(false);
  const [processingPaypal, setProcessingPaypal] = useState(false);
  const errorRef = useRef(null);
  const { paypalEnvironment, subscriptionType, requests } = props;
  const [formKey, setFormKey] = useState(genFormKey());

  // I'm pulling these off separately because they are only used in test.
  // If they're undefined, the real elements will be used, but if components
  // are passed in, we can render a slightly different version of this component
  // to use in Capybara tests without actually talking to either Stripe or Paypal.
  const { PaypalButton, StripePaymentElement } = props;

  useEffect(() => {
    if (subscriptionType && paypalActions.enable) {
      paypalActions.enable();
    }
  }, [paypalActions, subscriptionType]);

  useEffect(() => {
    setFormKey(genFormKey());
  }, [subscriptionType]);

  const scrollToError = () =>
    errorRef.current.scrollIntoView({ behavior: "smooth" });

  async function onSubmit(postData) {
    const paymentType = postData.paypal_token ? "paypal" : "stripe";
    const response = await requests.createSubscription(postData);

    const finalDestination = response.headers.get("Location");

    if (response.ok) {
      window.location.assign(finalDestination);
    } else {
      // we only return JSON for errors
      const json = await response.json();
      if (paymentType === "stripe") {
        setStripeErrorMessage(json.errorMessage);
        // Discard the old SetupIntent and issue a new one.
        if (json.reloadStripe) {
          setFormKey(genFormKey());
        }
      } else {
        setPaypalErrorMessage(json.errorMessage);
      }
      scrollToError();
      setDisabled(false);
      setProcessingStripe(false);
      setProcessingPaypal(false);
      paypalActions.enable();
      window.LitCharts.trackEvent(
        "payment-error",
        `${paymentType} onSubscribeFailed`,
        {
          extraData: {
            responseJSON: json
          }
        }
      );
    }
  }

  const paypalHandlers = {
    onAuthorize: async (data) => {
      setProcessingPaypal(true);
      onSubmit({
        paypal_token: helpers.extractPaypalExpressCheckoutToken(data),
        subscription: subscriptionType,
        subscription_origin: "sign-up"
      });
    },
    onCancel: () => {
      setDisabled(false);
      setProcessingPaypal(false);
    },
    onClick: () => {},
    onError: (error) => {
      const captures = error.message.match(/Error: (.*)/);
      setPaypalErrorMessage(captures[1]);
      setDisabled(false);
      setProcessingPaypal(false);
    },
    payment: async (resolve, reject) => {
      setDisabled(true);

      const response = await requests.createBillingAgreement(subscriptionType);
      const data = await response.json();
      if (response.ok) {
        resolve(data.token);
      } else {
        reject(data.errorMessage);
      }
    },
    validate: (actions) => {
      // We want to disable the button here. When the user selects a subscription type we'll enable it.
      actions.disable();
      setPaypalActions(actions);
    }
  };

  const stripeHandlers = {
    onError: (result) => {
      const { error } = result;
      setNonWalletErrors(error, setStripeErrorMessage);

      setDisabled(false);
      setProcessingStripe(false);
      paypalActions.enable();
    },
    onSubmission: () => {
      setDisabled(true);
      setProcessingStripe(true);
      paypalActions.disable();
      return true;
    },
    onSuccess: (result) => {
      const { id, status } = result.paymentIntent;
      onSubmit({
        payment_intent_id: id,
        payment_status: status,
        subscription: subscriptionType,
        subscription_origin: "sign-up"
      });
    }
  };

  return (
    <div
      ref={errorRef}
      className="user-subscribe-sell stripe-updates"
      data-testid="signup-payment-form"
    >
      <FullPaymentForm
        isPaymentIntent
        clientSecret={props.clientSecret}
        paypalHandlers={paypalHandlers}
        paypalEnvironment={paypalEnvironment}
        paypalErrorMessage={paypalErrorMessage}
        paypalProcessing={processingPaypal}
        stripeFormKey={formKey}
        stripeHandlers={stripeHandlers}
        stripeProcessing={processingStripe}
        stripeErrorMessage={stripeErrorMessage}
        disabled={disabled}
        subscriptionType={subscriptionType}
        PaypalButton={PaypalButton}
        StripePaymentElement={StripePaymentElement}
      />
    </div>
  );
}

const requests = {
  createBillingAgreement: (subscription) => {
    const url = "/paypal/billing_agreements";
    const fetchParams = {
      body: JSON.stringify({ subscription }),
      method: "POST"
    };
    return LcFetch(url, fetchParams);
  },
  createSubscription: (postData) => {
    const fetchParams = {
      body: JSON.stringify(postData),
      method: "POST"
    };
    return LcFetch("/subscriptions", fetchParams);
  }
};

export default function (props) {
  return <SignUpPaymentForm {...props} requests={requests} />;
}
